import { createSlice } from '@reduxjs/toolkit';

import { getEnv } from 'utils/utils';
import { handleNetworkChange } from 'store/appActions';
import { userLoggedIn, userLoggedOut } from 'store/appThunks';
import { DEMO, QUANTUM_DEMO } from 'utils/constants';
import { DEFAULT_TOKEN, setDemoToken, testing as testingDemoTokens } from 'utils/demoTokens';
import { CONFIG_SLICE_NAME } from '../slicesNames';
import * as configThunks from './configThunks';
import { combinedLocalConfig, initializeNetwork, localClientConfig } from './configUtils';

if (localClientConfig.EMBOLD_CLIENT === DEMO || localClientConfig.EMBOLD_CLIENT === QUANTUM_DEMO) {
  const token = localStorage.getItem('integrationToken');
  if (!Object.values(testingDemoTokens).includes(token)) setDemoToken(DEFAULT_TOKEN);
}

const initialState = {
  isLoading: false,
  success: false,
  error: null,
  appMounted: false,
  currentNetworkSlug: null,
  termsLastAccepted: localStorage.getItem('termsLastAccepted') || null,
  networkGroup: null,
  values: {
    ...combinedLocalConfig,
  },
};

const FUSION_DEV_URL = 'https://fusion.dev.emboldhealth.com/api/pg';

// setup local, testing and staging specifics
const env = getEnv();
if (process.env.NODE_ENV === 'development') {
  // determine if we are hitting fusion dev or local fusion
  const apiUrl = localStorage.getItem('devTools:apiUrl') || FUSION_DEV_URL;
  initialState.values.API_URL = apiUrl;
  if (apiUrl !== FUSION_DEV_URL) {
    initialState.values.API_TOKEN = `Token ${process.env.REACT_APP_LOCAL_FUSION_SUPERUSER_TOKEN}`;
  }
} else if (env === 'testing') {
  initialState.values.API_URL = FUSION_DEV_URL;
} else if (env === 'staging') {
  initialState.values.API_URL = 'https://fusion.staging.emboldhealth.com/api/pg';
}

const configSlice = createSlice({
  name: CONFIG_SLICE_NAME,
  initialState,
  reducers: {
    setNetworkSlug(state, action) {
      state.currentNetworkSlug = action.payload;
    },
    overrideValue(state, action) {
      if (process.env.NODE_ENV === 'development') {
        state.values = { ...state.values, ...action.payload };
      }
    },
    appMounted: (state) => {
      state.appMounted = true;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(configThunks.initializeConfig.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(configThunks.initializeConfig.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
        state.values = { ...state.values, ...action.payload };
      })
      .addCase(configThunks.initializeConfig.rejected, (state, action) => {
        state.isLoading = false;
        // error message could be passed with rejectWithValue which puts the message in the action.payload
        // OR errors can be thrown which puts them in action.error
        state.error = action.payload || action.error?.message || 'Unknown Error';
      });

    builder.addCase(handleNetworkChange, (state, action) => {
      const networkSlug = action.payload;
      state.currentNetworkSlug = networkSlug;
    });
    builder
      .addCase(userLoggedIn.fulfilled, (state, action) => {
        const { networkSlug, termsChecked = false } = action.payload;
        state.currentNetworkSlug = networkSlug;
        if (termsChecked) {
          const dateString = new Date(Date.now()).toISOString();
          state.termsLastAccepted = dateString;
          localStorage.setItem('termsLastAccepted', dateString);
        }
      })
      .addCase(userLoggedOut.fulfilled, (state) => {
        state.currentNetworkSlug = null;
        state.termsLastAccepted = null;
        localStorage.removeItem('termsLastAccepted');
      });

    builder.addCase(configThunks.initialize.fulfilled, initializeNetwork);
  },
});

export default configSlice;
export const { setNetworkSlug, appMounted } = configSlice.actions;
